import { css } from "glamor";
import React, { forwardRef } from "react";
import { Modal } from "semantic-ui-react";
import { Button, Link, colors, spacing, styles } from "../library";
import VimeoPlayer from "../library/VimeoPlayer";

const container = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  width: "100%",
  paddingBottom: "32.6%",
  "@media(max-width: 575px)": {
    paddingBottom: "68%",
  },
});
const bannerBaseClass = css({
  zIndex: 2,
  cursor: "pointer",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100%",
  backgroundPosition: "center",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  color: "red",
});
const htmlClass = css({
  ...styles.wrappingText,
  color: colors.greyShadow,
  whiteSpace: "pre-line",
  paddingTop: spacing.m,
  "& + &": {
    marginTop: spacing.m,
  },
});
const modalActionsStyle = {
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
};

const successMessageStyle = {
  color: "#57A314",
  marginTop: "16px",
  fontWeight: "bold",
  fontSize: "14px",
  textAlign: "center",
};

const errorMessageStyle = {
  color: "#D32F2F",
  marginTop: "16px",
  fontSize: "14px",
  textAlign: "center",
};

const Banner = forwardRef(
  ({
    banner,
    isLightbox,
    openLightbox,
    toggleLightbox,
    linkTarget,
    onEventCreate,
    onEventCreateAndLink,
    eventLoading,
    eventError,
    eventCreated,
    eventSuccess
  }, ref) => {
    const hasCTA = !!banner?.lightboxActionUrl || banner?.requiresEventCreation;
    const hasContent = banner?.url || banner?.lightboxDescription;
    const BannerComponent = isLightbox ? "div" : "a";

    const containerClass = css(container, {
      backgroundColor: banner.backgroundColor || colors.brandDark,
    });

    const bannerClass = css(bannerBaseClass, {
      backgroundImage: `url(${banner.imageUrl})`,
      "@media(max-width: 575px)": {
        backgroundImage: `url(${
          banner.imageMobileUrl ? banner.imageMobileUrl : banner.imageUrl
        })`,
      },
    });

    return (
      <BannerComponent className={containerClass} ref={ref}>
        <div className={bannerClass} />
        {isLightbox && hasContent && (
          <Modal open={openLightbox} onClose={toggleLightbox} closeIcon>
            {banner?.lightboxTitle && (
              <Modal.Header>{banner?.lightboxTitle}</Modal.Header>
            )}
            <Modal.Content>
              {banner?.url && <VimeoPlayer url={banner?.url} skipPreferences />}
              {banner?.lightboxDescription && (
                <Modal.Description>
                  <div
                    className={htmlClass}
                    dangerouslySetInnerHTML={{
                      __html: banner.lightboxDescription,
                    }}
                  ></div>
                </Modal.Description>
              )}
            </Modal.Content>
            {hasCTA && (
              <Modal.Actions style={modalActionsStyle}>
                {banner.ctaAction === 'external_link' && (
                  <Link
                    href={banner?.lightboxActionUrl}
                    target={linkTarget}
                    component={Button}
                    secondary
                    size="big"
                  >
                    {banner?.lightboxActionTitle || "Clique Aqui!"}
                  </Link>
                )}

                {banner.ctaAction === 'create_event' && (
                  <>
                    <Button
                      secondary
                      size="big"
                      loading={eventLoading}
                      disabled={eventLoading || eventCreated}
                      onClick={() => onEventCreate(banner.id)}
                    >
                      {eventCreated
                        ? banner?.eventEnrolledMessage || "✓ Você já está inscrito!"
                        : banner?.lightboxActionTitle || "Quero Participar!"}
                    </Button>
                    {eventSuccess && (
                      <div style={successMessageStyle}>
                        {banner?.eventSuccessMessage || "✓ Inscrição realizada com sucesso!"}
                      </div>
                    )}
                    {eventError && (
                      <div style={errorMessageStyle}>
                        {eventError}
                      </div>
                    )}
                  </>
                )}

                {banner.ctaAction === 'link_and_event' && (
                  <>
                    <Button
                      secondary
                      size="big"
                      loading={eventLoading}
                      disabled={eventLoading || eventCreated}
                      onClick={() => onEventCreateAndLink(banner.id, banner.lightboxActionUrl)}
                    >
                      {eventCreated
                        ? banner?.eventEnrolledMessage || "✓ Você já está inscrito!"
                        : banner?.lightboxActionTitle || "Quero Participar!"}
                    </Button>
                    {eventSuccess && (
                      <div style={successMessageStyle}>
                        {banner?.eventSuccessMessage || "✓ Inscrição realizada com sucesso!"}
                      </div>
                    )}
                    {eventError && (
                      <div style={errorMessageStyle}>
                        {eventError}
                      </div>
                    )}
                  </>
                )}
              </Modal.Actions>
            )}
          </Modal>
        )}
      </BannerComponent>
    );
  }
);

Banner.displayName = 'Banner';

export default Banner;
